import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Phone as PhoneIcon,
  TrendingUp as TrendingUpIcon,
  Message as MessageIcon,
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Instagram as InstagramIcon,
  Web as WebIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  Memory as MemoryIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import axios from '../config/axios';
import RealtimeFollowupStatus from '../components/RealtimeFollowupStatus';
import CampaignStepsManager from '../components/CampaignStepsManager';
import MediaUpload from '../components/MediaUpload';
import MemoryFollowupMonitor from '../components/MemoryFollowupMonitor';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Template {
  id: string;
  name: string;
  description?: string;
  message_text: string;
  channel_type: 'whatsapp' | 'telegram' | 'instagram' | 'webchat' | 'sms' | 'email' | 'all';
  status: 'active' | 'inactive';
  created_at: string;
  // Campos de mídia
  image_url?: string;
  audio_url?: string;
  video_url?: string;
  file_url?: string;
  media_url?: string; // Campo do backend
  media_type?: 'text' | 'image' | 'audio' | 'video' | 'file' | 'document';
  media_caption?: string;
}

interface CrmStage {
  id: string;
  name: string;
  description?: string;
  color: string;
  stage_order: number;
  is_final_stage: boolean;
  crm_pipelines: {
    name: string;
    color: string;
  };
}

interface Campaign {
  id: string;
  name: string;
  description?: string;
  channel_type: 'whatsapp' | 'telegram' | 'instagram' | 'webchat' | 'sms' | 'email' | 'all';
  max_followups: number;
  status: 'active' | 'inactive';
  followup_steps?: CampaignStep[];
  crm_stages?: string[];
  auto_target_inbox?: boolean;
}

interface CampaignStep {
  id: string;
  step_order: number;
  delay_hours: number;
  delay_days: number;
  followup_templates: {
    name: string;
    message_text: string;
  };
}

interface CallTemplate {
  id: string;
  name: string;
  description?: string;
  script_content: string;
  assistant_config_id?: string;
  status: 'active' | 'inactive';
  created_at: string;
}

interface VapiAssistant {
  id: string;
  name: string;
  description?: string;
  is_default: boolean;
  status: 'active' | 'inactive';
}


interface Stats {
  total_followups_sent: number;
  total_responses: number;
  pending_followups: number;
  response_rate: string;
  recent_executions: any[];
}

import { getApiUrl } from '../config/environment';

const API_BASE_URL = getApiUrl();

// Função para obter o nome do estágio baseado na configuração das variáveis de ambiente
const getStageName = (stageOrder: number, stagesConfig: any): string => {
  if (!stagesConfig) return 'Estágio';
  
  const stageMap: Record<number, string> = {
    1: stagesConfig.stage1?.name || 'Lead Novo',
    2: stagesConfig.stage2?.name || 'Contato Feito',
    3: stagesConfig.stage3?.name || 'Interessado',
    4: stagesConfig.stage4?.name || 'Depósito Realizado',
    5: stagesConfig.stage5?.name || 'Cliente',
    6: stagesConfig.stage6?.name || 'Perdido',
    7: stagesConfig.stage7?.name || 'Pré-Negociação',
    8: stagesConfig.stage8?.name || 'Negociação',
    9: stagesConfig.stage9?.name || 'Fechamento'
  };
  
  return stageMap[stageOrder] || `Estágio ${stageOrder}`;
};

const Followups: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Estados para Templates
  const [templates, setTemplates] = useState<Template[]>([]);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    message_text: '',
    channel_type: 'all' as 'whatsapp' | 'telegram' | 'instagram' | 'webchat' | 'sms' | 'email' | 'all',
    // Campos de mídia
    image_url: '',
    audio_url: '',
    video_url: '',
    file_url: '',
    media_type: 'text' as 'text' | 'image' | 'audio' | 'video' | 'file' | 'document',
    media_caption: ''
  });

  // Estados para Templates de Ligação
  const [callTemplates, setCallTemplates] = useState<CallTemplate[]>([]);
  const [vapiAssistants, setVapiAssistants] = useState<VapiAssistant[]>([]);
  const [openCallTemplateDialog, setOpenCallTemplateDialog] = useState(false);
  const [editingCallTemplate, setEditingCallTemplate] = useState<CallTemplate | null>(null);
  const [callTemplateForm, setCallTemplateForm] = useState({
    name: '',
    description: '',
    script_content: '',
    assistant_config_id: ''
  });

  // Estados para Campanhas
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [openCampaignDialog, setOpenCampaignDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    channel_type: 'all' as 'whatsapp' | 'telegram' | 'instagram' | 'webchat' | 'sms' | 'email' | 'all',
    max_followups: 3,
    crm_stages: [] as string[],
    auto_target_inbox: false
  });
  const [campaignSteps, setCampaignSteps] = useState<any[]>([]);

  // Estados para CRM Stages
  const [crmStages, setCrmStages] = useState<CrmStage[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [stageContacts, setStageContacts] = useState<{[key: string]: number}>({});
  const [stagesConfig, setStagesConfig] = useState<any>(null);



  // Estados para Estatísticas
  const [stats, setStats] = useState<Stats | null>(null);
  const [campaignStats, setCampaignStats] = useState<any[]>([]);
  const [templatePerformance, setTemplatePerformance] = useState<any[]>([]);
  const [detailedCampaignStats, setDetailedCampaignStats] = useState<any[]>([]);
  const [memoryStats, setMemoryStats] = useState<any>(null);




  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 0: // Dashboard
          await loadStats();
          break;
        case 1: // Templates
          await loadTemplates();
          break;
        case 2: // Campanhas
          await Promise.all([loadCampaigns(), loadCrmStages(), fetchStagesConfig()]);
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // =============================================
  // FUNÇÕES PARA ESTATÍSTICAS
  // =============================================

  const loadStats = async () => {
    try {
      // Tenta usar estatísticas do sistema em tempo real primeiro
      try {
        const [realtimeStatsRes, campaignStatsRes, templatePerfRes, detailedStatsRes, memoryStatsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/realtime-followups/stats`),
          axios.get(`${API_BASE_URL}/followups/stats/campaigns`),
          axios.get(`${API_BASE_URL}/followups/stats/templates`),
          axios.get(`${API_BASE_URL}/followups/stats/campaigns/detailed`),
          axios.get(`${API_BASE_URL}/followups/stats/memory`)
        ]);

        if (realtimeStatsRes.data.success) {
          setStats(realtimeStatsRes.data.data);
          setCampaignStats(campaignStatsRes.data.data || []);
          setTemplatePerformance(templatePerfRes.data.data || []);
          setDetailedCampaignStats(detailedStatsRes.data.data || []);
          setMemoryStats(memoryStatsRes.data.data || null);
          return;
        }
      } catch (realtimeError) {
        console.log('Realtime stats not available, using legacy stats');
      }

      // Fallback para estatísticas legadas
      const [overviewRes, campaignStatsRes, templatePerfRes, detailedStatsRes, memoryStatsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/followups/stats/overview`),
        axios.get(`${API_BASE_URL}/followups/stats/campaigns`),
        axios.get(`${API_BASE_URL}/followups/stats/templates`),
        axios.get(`${API_BASE_URL}/followups/stats/campaigns/detailed`),
        axios.get(`${API_BASE_URL}/followups/stats/memory`)
      ]);

      setStats(overviewRes.data.data);
      setCampaignStats(campaignStatsRes.data.data || []);
      setTemplatePerformance(templatePerfRes.data.data || []);
      setDetailedCampaignStats(detailedStatsRes.data.data || []);
      setMemoryStats(memoryStatsRes.data.data || null);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // =============================================
  // FUNÇÕES PARA TEMPLATES
  // =============================================

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/followups/templates`);
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  // =============================================
  // FUNÇÕES PARA TEMPLATES DE LIGAÇÃO
  // =============================================

  const loadCallTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vapi/call-templates`);
      setCallTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error loading call templates:', error);
    }
  };

  const loadVapiAssistants = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vapi/assistants`);
      setVapiAssistants(response.data.data || []);
    } catch (error) {
      console.error('Error loading VAPI assistants:', error);
    }
  };

  const handleCreateCallTemplate = async () => {
    try {
      if (editingCallTemplate) {
        await axios.put(`${API_BASE_URL}/vapi/call-templates/${editingCallTemplate.id}`, callTemplateForm);
      } else {
        await axios.post(`${API_BASE_URL}/vapi/call-templates`, callTemplateForm);
      }
      
      setOpenCallTemplateDialog(false);
      setEditingCallTemplate(null);
      resetCallTemplateForm();
      await loadCallTemplates();
    } catch (error) {
      console.error('Error creating/updating call template:', error);
      setError('Erro ao salvar template de ligação');
    }
  };

  const handleEditCallTemplate = (template: CallTemplate) => {
    setEditingCallTemplate(template);
    setCallTemplateForm({
      name: template.name,
      description: template.description || '',
      script_content: template.script_content,
      assistant_config_id: template.assistant_config_id || ''
    });
    setOpenCallTemplateDialog(true);
  };

  const handleDeleteCallTemplate = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este template de ligação?')) {
      try {
        await axios.delete(`${API_BASE_URL}/vapi/call-templates/${id}`);
        await loadCallTemplates();
      } catch (error) {
        console.error('Error deleting call template:', error);
        setError('Erro ao excluir template de ligação');
      }
    }
  };

  const resetCallTemplateForm = () => {
    setCallTemplateForm({
      name: '',
      description: '',
      script_content: '',
      assistant_config_id: ''
    });
  };

  const handleCreateTemplate = async () => {
    try {
      // Mapear campos de mídia específicos para o formato do backend
      let media_url = '';
      let media_type = templateForm.media_type;
      
      // Determinar a URL da mídia baseada no tipo
      switch (templateForm.media_type) {
        case 'image':
          media_url = templateForm.image_url || '';
          break;
        case 'audio':
          media_url = templateForm.audio_url || '';
          break;
        case 'video':
          media_url = templateForm.video_url || '';
          break;
        case 'file':
          media_url = templateForm.file_url || '';
          break;
        default:
          media_url = '';
          media_type = 'text';
      }
      
      // Preparar dados para envio
      const templateData = {
        name: templateForm.name,
        description: templateForm.description,
        message_text: templateForm.message_text,
        channel_type: templateForm.channel_type,
        media_url: media_url || null,
        media_type: media_type,
        media_caption: templateForm.media_caption || null
      };
      
      console.log('Enviando dados do template:', templateData);
      
      if (editingTemplate) {
        await axios.put(`${API_BASE_URL}/followups/templates/${editingTemplate.id}`, templateData);
      } else {
        await axios.post(`${API_BASE_URL}/followups/templates`, templateData);
      }
      
      setOpenTemplateDialog(false);
      setEditingTemplate(null);
      resetTemplateForm();
      await loadTemplates();
    } catch (error) {
      console.error('Error creating/updating template:', error);
      setError('Erro ao salvar template');
    }
  };

  const handleEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    
    // Mapear media_url do backend para os campos específicos do frontend
    let image_url = '';
    let audio_url = '';
    let video_url = '';
    let file_url = '';
    
    if (template.media_url) {
      switch (template.media_type) {
        case 'image':
          image_url = template.media_url;
          break;
        case 'audio':
          audio_url = template.media_url;
          break;
        case 'video':
          video_url = template.media_url;
          break;
        case 'file':
        case 'document':
          file_url = template.media_url;
          break;
      }
    }
    
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      message_text: template.message_text,
      channel_type: template.channel_type,
      // Campos de mídia mapeados
      image_url: image_url,
      audio_url: audio_url,
      video_url: video_url,
      file_url: file_url,
      media_type: template.media_type || 'text',
      media_caption: template.media_caption || ''
    });
    setOpenTemplateDialog(true);
  };

  const handleDeleteTemplate = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este template?')) {
      try {
        await axios.delete(`${API_BASE_URL}/followups/templates/${id}`);
        await loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
        setError('Erro ao excluir template');
      }
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita e removerá todos os dados relacionados.')) {
      try {
        setLoading(true);
        await axios.delete(`${API_BASE_URL}/followups/campaigns/${id}`);
        await loadCampaigns();
        setError('');
      } catch (error) {
        console.error('Error deleting campaign:', error);
        setError('Erro ao excluir campanha');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      description: '',
      message_text: '',
      channel_type: 'all',
      // Campos de mídia
      image_url: '',
      audio_url: '',
      video_url: '',
      file_url: '',
      media_type: 'text',
      media_caption: ''
    });
  };

  // =============================================
  // FUNÇÕES PARA CAMPANHAS
  // =============================================

  const loadCampaigns = async () => {
    try {
      console.log('Loading campaigns...');
      const response = await axios.get(`${API_BASE_URL}/followups/campaigns`);
      console.log('Campaigns response:', response.data);
      const campaignsData = response.data.data || [];
      console.log('Campaigns data:', campaignsData);
      
      // Verificar se há campanhas com status ativo
      const activeCampaigns = campaignsData.filter((c: any) => c.status === 'active');
      console.log('Active campaigns:', activeCampaigns);
      
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const loadCrmStages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/followups/crm-stages`);
      setCrmStages(response.data.data || []);
      
      // Carregar contatos de cada estágio (sem filtro de inbox por padrão)
      const contactsData: {[key: string]: number} = {};
      for (const stage of response.data.data || []) {
        try {
          const contactsResponse = await axios.get(`${API_BASE_URL}/followups/crm-stages/${stage.id}/contacts`);
          contactsData[stage.id] = contactsResponse.data.data?.length || 0;
        } catch (error) {
          contactsData[stage.id] = 0;
        }
      }
      setStageContacts(contactsData);
    } catch (error) {
      console.error('Error loading CRM stages:', error);
    }
  };

  // Função para buscar configurações dos estágios
  const fetchStagesConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/crm/stages-config`);
      if (response.data.success) {
        setStagesConfig(response.data.data);
      }
    } catch (err: any) {
      console.error('❌ Erro ao buscar configurações dos estágios:', err);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      if (editingCampaign) {
        await axios.put(`${API_BASE_URL}/followups/campaigns/${editingCampaign.id}`, campaignForm);
      } else {
        await axios.post(`${API_BASE_URL}/followups/campaigns`, {
          ...campaignForm,
          steps: campaignSteps
        });
      }
      
      setOpenCampaignDialog(false);
      setEditingCampaign(null);
      resetCampaignForm();
      await loadCampaigns();
    } catch (error) {
      console.error('Error creating/updating campaign:', error);
      setError('Erro ao salvar campanha');
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '',
      description: '',
      channel_type: 'all',
      max_followups: 3,
      crm_stages: [],
      auto_target_inbox: false
    });
    setCampaignSteps([]);
    setSelectedStages([]);
  };

  const handleActivateCampaign = async (campaignId: string) => {
    try {
      console.log('Activating campaign:', campaignId);
      setLoading(true);
      
      const campaign = campaigns.find(c => c.id === campaignId);
      if (!campaign) {
        console.error('Campaign not found:', campaignId);
        setError('Campanha não encontrada');
        return;
      }

      console.log('Campaign found:', campaign);
      console.log('CRM stages:', campaign.crm_stages);
      
      const payload = {
        use_crm_stages: campaign.crm_stages && campaign.crm_stages.length > 0
      };
      
      console.log('Activation payload:', payload);
      console.log('API URL:', `${API_BASE_URL}/followups/campaigns/${campaignId}/activate`);

      const response = await axios.post(`${API_BASE_URL}/followups/campaigns/${campaignId}/activate`, payload);
      
      console.log('Activation response:', response.data);

      if (response.data.success) {
        console.log('Campaign activated successfully');
        await loadCampaigns();
        setError('');
      } else {
        console.error('Activation failed:', response.data);
        setError(response.data.error || 'Erro ao ativar campanha');
      }
    } catch (error: any) {
      console.error('Error activating campaign:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.error || 'Erro ao ativar campanha');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateCampaign = async (campaignId: string) => {
    try {
      console.log('Deactivating campaign:', campaignId);
      setLoading(true);

      const response = await axios.post(`${API_BASE_URL}/followups/campaigns/${campaignId}/deactivate`);
      
      console.log('Deactivation response:', response.data);

      if (response.data.success) {
        console.log('Campaign deactivated successfully');
        await loadCampaigns();
        setError('');
      } else {
        console.error('Deactivation failed:', response.data);
        setError(response.data.error || 'Erro ao desativar campanha');
      }
    } catch (error: any) {
      console.error('Error deactivating campaign:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.error || 'Erro ao desativar campanha');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      description: campaign.description || '',
      channel_type: campaign.channel_type || 'all',
      max_followups: campaign.max_followups,
      crm_stages: campaign.crm_stages || [],
      auto_target_inbox: campaign.auto_target_inbox || false
    });
    setSelectedStages(campaign.crm_stages || []);
    setOpenCampaignDialog(true);
  };




  // =============================================
  // FUNÇÕES AUXILIARES
  // =============================================

  const getChannelIcon = (channelType: string) => {
    switch (channelType) {
      case 'whatsapp': return <WhatsAppIcon sx={{ color: '#25D366' }} />;
      case 'telegram': return <TelegramIcon sx={{ color: '#229ED9' }} />;
      case 'instagram': return <InstagramIcon sx={{ color: '#E4405F' }} />;
      case 'webchat': return <WebIcon sx={{ color: '#6366F1' }} />;
      case 'email': return <EmailIcon sx={{ color: '#EA4335' }} />;
      case 'sms': return <SmsIcon sx={{ color: '#FF9800' }} />;
      default: return <MessageIcon sx={{ color: '#666' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'sent': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      case 'skipped': return 'info';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>
      {/* Header */}
      <Box className="glass-blue-card rounded-xl p-4 mb-6" sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: '#000000',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 0.5,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Sistema de Remarketing
          </Typography>
          <Typography variant="body2" sx={{ 
            color: '#64748b',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Automatize o acompanhamento de leads com mensagens personalizadas
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box className="glass-blue-card rounded-xl mb-6" sx={{ overflow: 'hidden' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b82f6',
              height: 3,
            },
            '& .MuiTab-root': {
              color: '#64748b',
              '&.Mui-selected': {
                color: '#3b82f6',
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab icon={<AnalyticsIcon />} iconPosition="start" label="Dashboard" />
          <Tab icon={<MessageIcon />} iconPosition="start" label="Templates" />
          <Tab icon={<CampaignIcon />} iconPosition="start" label="Campanhas" />
        </Tabs>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Dashboard Tab - Sistema de Memória */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ 
            color: '#000000', 
            fontWeight: 600,
            mb: 2,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Sistema de Followup - Memória
          </Typography>
        </Box>
        
        <div className="glass-blue-card rounded-xl mb-6 p-4">
          <Alert severity="info" sx={{ 
            mb: 0,
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            '& .MuiAlert-icon': {
              color: '#3b82f6'
            }
          }}>
            <Typography variant="body2" sx={{ color: '#000000', mb: 1 }}>
              <strong>Sistema de Memória Atômico:</strong> Este sistema processa followups em memória para máxima performance e controle atômico.
            </Typography>
            <Typography variant="body2" sx={{ color: '#000000', mb: 0.5 }}>
              • <strong>Processamento em Tempo Real:</strong> Followups são processados a cada 10 segundos
            </Typography>
            <Typography variant="body2" sx={{ color: '#000000', mb: 0.5 }}>
              • <strong>Controle Atômico:</strong> Cada lead recebe cada step apenas uma vez
            </Typography>
            <Typography variant="body2" sx={{ color: '#000000', mb: 0.5 }}>
              • <strong>Baseado em Atividade:</strong> Followups são disparados baseados na última mensagem do bot
            </Typography>
            <Typography variant="body2" sx={{ color: '#000000' }}>
              • <strong>Fila de Execução:</strong> Sistema mantém fila ordenada por tempo de execução
            </Typography>
          </Alert>
        </div>
        
        <MemoryFollowupMonitor />
      </TabPanel>

      {/* Templates Tab */}
      <TabPanel value={activeTab} index={1}>
        <Box className="glass-blue-card rounded-xl p-4 mb-6" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
            Templates de Mensagens
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenTemplateDialog(true)}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
              }
            }}
          >
            Novo Template
          </Button>
        </Box>

        <div className="glass-blue-card rounded-xl mb-8">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    Nome
                  </TableCell>
                  <TableCell sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    Canal
                  </TableCell>
                  <TableCell sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    Tipo
                  </TableCell>
                  <TableCell sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    Prévia da Mensagem
                  </TableCell>
                  <TableCell sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ 
                    color: '#000000',
                    fontWeight: 600,
                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow 
                  key={template.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.05)',
                    },
                    borderBottom: '1px solid rgba(59, 130, 246, 0.1)'
                  }}
                >
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ color: '#000000' }}>{template.name}</Typography>
                    {template.description && (
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {template.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getChannelIcon(template.channel_type)}
                      <Typography sx={{ ml: 1, textTransform: 'capitalize', color: '#475569' }}>
                        {template.channel_type}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {template.media_type === 'text' ? (
                        <Chip 
                          label="Texto" 
                          size="small" 
                          color="default" 
                          icon={<MessageIcon />}
                          sx={{ bgcolor: 'grey.700', color: 'grey.100' }}
                        />
                      ) : template.media_type === 'image' ? (
                        <Chip 
                          label="Imagem" 
                          size="small" 
                          color="primary" 
                          icon={<span>📷</span>}
                          sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText' }}
                        />
                      ) : template.media_type === 'audio' ? (
                        <Chip 
                          label="Áudio" 
                          size="small" 
                          color="secondary" 
                          icon={<span>🎵</span>}
                          sx={{ bgcolor: 'secondary.dark', color: 'secondary.contrastText' }}
                        />
                      ) : template.media_type === 'video' ? (
                        <Chip 
                          label="Vídeo" 
                          size="small" 
                          color="warning" 
                          icon={<span>🎬</span>}
                          sx={{ bgcolor: 'warning.dark', color: 'warning.contrastText' }}
                        />
                      ) : template.media_type === 'file' ? (
                        <Chip 
                          label="Arquivo" 
                          size="small" 
                          color="info" 
                          icon={<span>📎</span>}
                          sx={{ bgcolor: 'info.dark', color: 'info.contrastText' }}
                        />
                      ) : (
                        <Chip 
                          label="Texto" 
                          size="small" 
                          color="default" 
                          icon={<MessageIcon />}
                          sx={{ bgcolor: 'grey.700', color: 'grey.100' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ maxWidth: 200, color: '#ffffff' }}>
                        {template.message_text.substring(0, 50)}...
                      </Typography>
                      {template.media_type && template.media_type !== 'text' && (
                        <Typography variant="caption" sx={{ 
                          fontStyle: 'italic',
                          bgcolor: 'rgba(59, 130, 246, 0.1)',
                          color: '#475569',
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5,
                          display: 'inline-block',
                          border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}>
                          + {template.media_type === 'image' ? 'Imagem' : 
                              template.media_type === 'audio' ? 'Áudio' : 
                              template.media_type === 'video' ? 'Vídeo' : 'Arquivo'}
                          {template.media_caption && `: ${template.media_caption.substring(0, 30)}...`}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={template.status === 'active' ? 'Ativo' : 'Inativo'}
                      color={template.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                        className="text-violet-400 hover:text-violet-300 hover:bg-violet-500/10"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </div>
      </TabPanel>

      {/* Campaigns Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box className="glass-blue-card rounded-xl p-4 mb-6" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
            Campanhas de Remarketing
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCampaignDialog(true)}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
              }
            }}
          >
            Nova Campanha
          </Button>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {campaigns.map((campaign) => (
            <Box key={campaign.id}>
              <Box className="glass-blue-card rounded-xl">
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getChannelIcon(campaign.channel_type)}
                    <Typography variant="h6" sx={{ ml: 1, color: '#000000' }}>
                      {campaign.name}
                    </Typography>
                  </Box>
                  
                  {campaign.description && (
                    <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                      {campaign.description}
                    </Typography>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      <strong>Canal:</strong> {campaign.channel_type}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      <strong>Máx. Remarketing:</strong> {campaign.max_followups}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      <strong>Steps:</strong> {campaign.followup_steps?.length || 0}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={campaign.status === 'active' ? 'Ativa' : 'Inativa'}
                        color={campaign.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                      {campaign.crm_stages && campaign.crm_stages.length > 0 && (
                        <Chip
                          label={`CRM: ${campaign.crm_stages.length} estágio(s)`}
                          color="primary"
                          size="small"
                          variant="outlined"
                          icon={<BusinessIcon />}
                        />
                      )}
                    </Box>
                    <Box>
                      {campaign.status !== 'active' && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleActivateCampaign(campaign.id)}
                          title="Ativar campanha"
                          disabled={loading}
                          sx={{ color: '#10b981', '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.1)' } }}
                        >
                          {loading ? <CircularProgress size={16} /> : <PlayIcon />}
                        </IconButton>
                      )}
                      {campaign.status === 'active' && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeactivateCampaign(campaign.id)}
                          title="Desativar campanha"
                          disabled={loading}
                          sx={{ color: '#ef4444', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' } }}
                        >
                          {loading ? <CircularProgress size={16} /> : <StopIcon />}
                        </IconButton>
                      )}
                      <IconButton 
                        size="small"
                        onClick={() => handleEditCampaign(campaign)}
                        title="Editar campanha"
                        sx={{ color: '#3b82f6', '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.1)' } }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        title="Excluir campanha"
                        disabled={loading}
                        sx={{ color: '#ef4444', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </TabPanel>

      {/* Schedules Tab */}
      <TabPanel value={activeTab} index={3}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Resumo de Agendamentos</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}

          >
            Atualizar
          </Button>
        </Box>



        {/* Estatísticas por Campanha */}
        <Typography variant="h6" sx={{ mb: 3 }}>
          Estatísticas por Campanha
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {campaigns.map((campaign) => {
                          

            return (
              <Card key={campaign.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getChannelIcon(campaign.channel_type)}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {campaign.name}
                    </Typography>
                  </Box>
                  



                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Estatísticas Detalhadas por Campanha */}
        <Typography variant="h6" sx={{ mb: 3, mt: 4 }}>
          📊 Estatísticas Detalhadas por Campanha
        </Typography>
        
        {detailedCampaignStats.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            {detailedCampaignStats.map((campaign) => (
              <Box key={campaign.campaign_id} className="glass-blue-card rounded-xl p-4">
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getChannelIcon(campaign.channel_type)}
                  <Typography variant="h6" sx={{ ml: 1, color: '#000000', fontWeight: '600' }}>
                    {campaign.campaign_name}
                  </Typography>
                </Box>
                  
                  {/* Estatísticas Principais */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(59, 130, 246, 0.08)', borderRadius: 1, border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                      <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                        {campaign.total_messages_sent || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: '500' }}>
                        Mensagens Enviadas
                      </Typography>
                    </Box>
                    
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(59, 130, 246, 0.08)', borderRadius: 1, border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                      <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                        {campaign.unique_contacts_reached || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: '500' }}>
                        Contatos Únicos
                      </Typography>
                    </Box>
                  </Box>

                  {/* Estatísticas por Template */}
                  {campaign.template_stats && campaign.template_stats.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: '#000000', mb: 1, fontWeight: '600' }}>
                        📝 Por Template:
                      </Typography>
                      {campaign.template_stats.map((template: any, index: number) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1.5,
                          bgcolor: 'rgba(59, 130, 246, 0.08)',
                          borderRadius: 1,
                          mb: 0.5,
                          border: '1px solid rgba(59, 130, 246, 0.2)'
                        }}>
                          <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem', fontWeight: '500' }}>
                            {template.template_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 'bold' }}>
                            {template.messages_sent} envios
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Estatísticas por Canal */}
                  {campaign.channel_stats && Object.keys(campaign.channel_stats).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: '#000000', mb: 1, fontWeight: '600' }}>
                        📱 Por Canal:
                      </Typography>
                      {Object.entries(campaign.channel_stats).map(([channel, count]) => (
                        <Box key={channel} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1.5,
                          bgcolor: 'rgba(59, 130, 246, 0.08)',
                          borderRadius: 1,
                          mb: 0.5,
                          border: '1px solid rgba(59, 130, 246, 0.2)'
                        }}>
                          <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem', textTransform: 'capitalize', fontWeight: '500' }}>
                            {channel}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 'bold' }}>
                            {count as number} envios
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Status da Campanha */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Chip 
                      label={campaign.status === 'active' ? 'Ativa' : 'Inativa'} 
                      color={campaign.status === 'active' ? 'success' : 'default'}
                      size="small"
                      sx={{ 
                        fontWeight: '500',
                        '&.MuiChip-success': {
                          backgroundColor: '#d4edda',
                          color: '#155724'
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: '500', textTransform: 'capitalize' }}>
                      {campaign.channel_type}
                    </Typography>
                  </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Alert severity="info">
            <Typography variant="body2">
              Nenhuma estatística detalhada de campanha encontrada. As estatísticas aparecerão aqui quando houver campanhas ativas com mensagens enviadas.
            </Typography>
          </Alert>
        )}

        {/* Estatísticas do Sistema em Memória */}
        {memoryStats && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              🧠 Sistema de Followup em Memória
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Box className="glass-blue-card rounded-xl p-4" sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                  {memoryStats.memory_stats?.totalLeads || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Total de Leads
                </Typography>
              </Box>
              
              <Box className="glass-blue-card rounded-xl p-4" sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                  {memoryStats.memory_stats?.followupsSent || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Followups Enviados
                </Typography>
              </Box>
              
              <Box className="glass-blue-card rounded-xl p-4" sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                  {memoryStats.memory_stats?.queuedFollowups || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Na Fila
                </Typography>
              </Box>
              
              <Box className="glass-blue-card rounded-xl p-4" sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#000000', fontWeight: 'bold' }}>
                  {memoryStats.memory_stats?.responsesReceived || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Respostas Recebidas
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        
      </TabPanel>


      {/* Template Dialog */}
      <Dialog 
        open={openTemplateDialog} 
        onClose={() => setOpenTemplateDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(31, 41, 55, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff',
          fontWeight: 600,
          borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          pb: 2
        }}>
          {editingTemplate ? 'Editar Template' : 'Novo Template'}
        </DialogTitle>
        <DialogContent sx={{ color: '#000000', pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome"
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              fullWidth
            />
            
            <TextField
              label="Descrição (opcional)"
              value={templateForm.description}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Canal</InputLabel>
              <Select
                value={templateForm.channel_type}
                onChange={(e) => setTemplateForm({ ...templateForm, channel_type: e.target.value as any })}
                label="Canal"
              >
                <MenuItem value="all">Todos os Canais</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="telegram">Telegram</MenuItem>
                <MenuItem value="instagram">Instagram</MenuItem>
                <MenuItem value="webchat">WebChat</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Mensagem"
              value={templateForm.message_text}
              onChange={(e) => setTemplateForm({ ...templateForm, message_text: e.target.value })}
              multiline
              rows={6}
              fullWidth
              helperText="Use {{nome}}, {{email}}, {{telefone}} para personalizar"
            />

            {/* Seção de Mídia */}
            <Box sx={{ 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 2, 
              p: 2, 
              bgcolor: 'background.paper',
              boxShadow: 1
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                📎 Anexos de Mídia (Opcional)
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tipo de Mídia</InputLabel>
                <Select
                  value={templateForm.media_type}
                  onChange={(e) => setTemplateForm({ ...templateForm, media_type: e.target.value as any })}
                  label="Tipo de Mídia"
                >
                  <MenuItem value="text">Apenas Texto</MenuItem>
                  <MenuItem value="image">Imagem</MenuItem>
                  <MenuItem value="audio">Áudio</MenuItem>
                  <MenuItem value="video">Vídeo</MenuItem>
                  <MenuItem value="file">Arquivo</MenuItem>
                </Select>
              </FormControl>

              {templateForm.media_type === 'image' && (
                <MediaUpload
                  mediaType="image"
                  value={templateForm.image_url}
                  onChange={(value) => setTemplateForm({ ...templateForm, image_url: value })}
                  placeholder="https://exemplo.com/imagem.jpg"
                  helperText="Cole a URL da imagem ou faça upload de um arquivo local"
                />
              )}

              {templateForm.media_type === 'audio' && (
                <MediaUpload
                  mediaType="audio"
                  value={templateForm.audio_url}
                  onChange={(value) => setTemplateForm({ ...templateForm, audio_url: value })}
                  placeholder="https://exemplo.com/audio.mp3"
                  helperText="Cole a URL do áudio ou faça upload de um arquivo local"
                />
              )}

              {templateForm.media_type === 'video' && (
                <MediaUpload
                  mediaType="video"
                  value={templateForm.video_url}
                  onChange={(value) => setTemplateForm({ ...templateForm, video_url: value })}
                  placeholder="https://exemplo.com/video.mp4"
                  helperText="Cole a URL do vídeo ou faça upload de um arquivo local"
                />
              )}

              {templateForm.media_type === 'file' && (
                <MediaUpload
                  mediaType="file"
                  value={templateForm.file_url}
                  onChange={(value) => setTemplateForm({ ...templateForm, file_url: value })}
                  placeholder="https://exemplo.com/documento.pdf"
                  helperText="Cole a URL do arquivo ou faça upload de um arquivo local"
                />
              )}

              {(templateForm.media_type === 'image' || templateForm.media_type === 'video') && (
                <TextField
                  label="Legenda da Mídia"
                  value={templateForm.media_caption}
                  onChange={(e) => setTemplateForm({ ...templateForm, media_caption: e.target.value })}
                  fullWidth
                  placeholder="Descrição da imagem/vídeo"
                  helperText="Texto que acompanhará a mídia (opcional)"
                  multiline
                  rows={2}
                />
              )}

              <Typography variant="body2" sx={{ 
                mt: 2, 
                color: 'text.secondary', 
                fontSize: '0.875rem',
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                💡 <strong>Dica:</strong> O sistema baixará automaticamente o arquivo, enviará para todos os leads elegíveis e depois o deletará.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid rgba(59, 130, 246, 0.2)',
          pt: 2,
          px: 3,
          pb: 2
        }}>
          <Button 
            onClick={() => setOpenTemplateDialog(false)}
            sx={{ color: '#000000' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleCreateTemplate} 
            variant="contained"
            className="bg-violet-600 hover:bg-violet-700 text-white"
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
              }
            }}
          >
            {editingTemplate ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Dialog */}
      <Dialog open={openCampaignDialog} onClose={() => setOpenCampaignDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingCampaign ? 'Editar Campanha' : 'Nova Campanha'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome"
              value={campaignForm.name}
              onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
              fullWidth
            />
            
            <TextField
              label="Descrição (opcional)"
              value={campaignForm.description}
              onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Canal</InputLabel>
              <Select
                value={campaignForm.channel_type}
                onChange={(e) => setCampaignForm({ ...campaignForm, channel_type: e.target.value as any })}
                label="Canal"
              >
                <MenuItem value="all">Todos os Canais</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="telegram">Telegram</MenuItem>
                <MenuItem value="instagram">Instagram</MenuItem>
                <MenuItem value="webchat">WebChat</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Máximo de Remarketing"
              type="number"
              value={campaignForm.max_followups}
              onChange={(e) => setCampaignForm({ ...campaignForm, max_followups: parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 10 }}
              fullWidth
            />

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1 }} />
              Integração com CRM
            </Typography>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  Selecionar Estágios do CRM ({campaignForm.crm_stages?.length || 0} selecionados)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {crmStages.map((stage) => (
                    <FormControlLabel
                      key={stage.id}
                      control={
                        <Checkbox
                          checked={campaignForm.crm_stages?.includes(stage.id) || false}
                          onChange={(e) => {
                            const newStages = e.target.checked
                              ? [...(campaignForm.crm_stages || []), stage.id]
                              : (campaignForm.crm_stages || []).filter(id => id !== stage.id);
                            setCampaignForm({ ...campaignForm, crm_stages: newStages });
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: stage.color,
                                mr: 1
                              }}
                            />
                            <Typography variant="body2">
                              {getStageName(stage.stage_order, stagesConfig) || stage.name}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${stageContacts[stage.id] || 0} leads`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </Box>
                      }
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            <FormControlLabel
              control={
                <Checkbox
                  checked={campaignForm.auto_target_inbox}
                  onChange={(e) => setCampaignForm({ ...campaignForm, auto_target_inbox: e.target.checked })}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="body2">
                    Incluir apenas leads que estão no inbox (com conversas ativas)
                  </Typography>
                </Box>
              }
            />

            {campaignForm.crm_stages && campaignForm.crm_stages.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Campanha configurada para:</strong>
                </Typography>
                <Typography variant="body2">
                  • {campaignForm.crm_stages.length} estágio(s) do CRM selecionado(s)
                </Typography>
                <Typography variant="body2">
                  • {campaignForm.auto_target_inbox ? 'Apenas leads no inbox' : 'Todos os leads dos estágios'}
                </Typography>
                <Typography variant="body2">
                  • Total estimado: {campaignForm.crm_stages.reduce((total, stageId) => total + (stageContacts[stageId] || 0), 0)} leads
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <MessageIcon sx={{ mr: 1 }} />
              Configurar Sequência de Mensagens
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Configure a sequência de mensagens que serão enviadas nesta campanha. Cada step representa uma mensagem com um delay específico.
              </Typography>
            </Alert>

            {editingCampaign && (
              <CampaignStepsManager 
                campaignId={editingCampaign.id}
                onStepsChange={(steps) => {
                  setCampaignSteps(steps);
                  console.log('Campaign steps updated:', steps);
                }}
              />
            )}

            {!editingCampaign && (
              <Alert severity="warning">
                <Typography variant="body2">
                  A sequência de mensagens pode ser configurada após a criação da campanha.
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            


          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid rgba(59, 130, 246, 0.2)',
          pt: 2,
          px: 3,
          pb: 2
        }}>
          <Button 
            onClick={() => setOpenCampaignDialog(false)}
            sx={{ color: '#000000' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleCreateCampaign} 
            variant="contained"
            className="bg-violet-600 hover:bg-violet-700 text-white"
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
              }
            }}
          >
            {editingCampaign ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Followups; 